import type {
  ApprovalPolicy,
  ApproverSource,
  CreateApprovalPolicyInput,
} from '@clockwise/clients/approval-policies';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ApprovalConditionEditor,
  conditionsFromServer,
  conditionsToServer,
  type EditorCondition,
} from '@/components/custom/approval-condition-editor';
import { ApproverSourcePicker } from '@/components/custom/approver-source-picker';
import { RequirePermission } from '@/components/custom/require-permission';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { approvalPoliciesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Create AND edit share this one sheet — pass `policy` (the row from the
 * list, which already carries `conditions`/`steps`) to edit it in place,
 * omit it to create. Both the list's "New Policy" button and each row's
 * click open this instead of the old `/setup/approval-policies/$id`
 * full-page route, which existed only to duplicate this form and has been
 * removed. The list already fetches full `ApprovalPolicy` rows (drag-reorder
 * needs the complete set), so no extra fetch is needed here.
 */

interface DraftStep {
  _uiKey: string;
  approvalType: 'any' | 'all';
  approverSources: ApproverSource[];
}

interface DraftPolicy {
  name: string;
  description: string;
  isActive: boolean;
  autoApprove: boolean;
  condition: EditorCondition;
  steps: DraftStep[];
}

let stepKeyCounter = 0;
function newStepKey(): string {
  stepKeyCounter += 1;
  return `step-${stepKeyCounter}`;
}

const emptyDraft = (): DraftPolicy => ({
  name: '',
  description: '',
  isActive: true,
  autoApprove: false,
  condition: { kind: 'none' },
  steps: [{ _uiKey: newStepKey(), approvalType: 'any', approverSources: [] }],
});

function toDraft(policy: ApprovalPolicy): DraftPolicy {
  // Auto-approve policies arrive with `steps = []`; seed the local draft
  // with a starter step anyway so toggling back to multi-step doesn't dump
  // the user into an editor with no rows. The starter is discarded on save
  // while auto-approve stays on.
  const steps =
    policy.steps.length > 0
      ? policy.steps.map((s) => ({
          _uiKey: newStepKey(),
          approvalType: s.approvalType,
          approverSources: s.approverSources,
        }))
      : [{ _uiKey: newStepKey(), approvalType: 'any' as const, approverSources: [] }];
  return {
    name: policy.name,
    description: policy.description ?? '',
    isActive: policy.isActive,
    autoApprove: policy.autoApprove,
    condition: conditionsFromServer(policy.conditions),
    steps,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Row being edited; null for create. */
  policy: ApprovalPolicy | null;
}

export function ApprovalPolicyFormSheet({ open, onOpenChange, policy }: Props) {
  const isEdit = policy !== null;
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<DraftPolicy>(() => emptyDraft());
  const [initialDraft, setInitialDraft] = useState<DraftPolicy>(() => emptyDraft());

  useEffect(() => {
    if (!open) return;
    const next = policy ? toDraft(policy) : emptyDraft();
    setDraft(next);
    setInitialDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.id]);

  const createMutation = useMutation({
    mutationFn: (input: CreateApprovalPolicyInput) => approvalPoliciesApi.create(input),
    onSuccess: () => {
      toast.success('Policy created');
      queryClient.invalidateQueries({ queryKey: queryKeys.approvalPolicies.all });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patchMutation = useMutation({
    mutationFn: (input: CreateApprovalPolicyInput) => {
      if (!policy) throw new Error('Policy not loaded');
      return approvalPoliciesApi.patch(policy.id, input);
    },
    onSuccess: () => {
      toast.success('Policy saved');
      queryClient.invalidateQueries({ queryKey: queryKeys.approvalPolicies.all });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isSaving = createMutation.isPending || patchMutation.isPending;

  const setStep = (index: number, next: DraftStep) => {
    setDraft((d) => ({
      ...d,
      steps: d.steps.map((s, i) => (i === index ? next : s)),
    }));
  };

  const addStep = () =>
    setDraft((d) => ({
      ...d,
      steps: [...d.steps, { _uiKey: newStepKey(), approvalType: 'any', approverSources: [] }],
    }));

  const removeStep = (index: number) =>
    setDraft((d) => ({
      ...d,
      steps: d.steps.filter((_, i) => i !== index),
    }));

  const moveStep = (index: number, dir: 'up' | 'down') => {
    setDraft((d) => {
      const next = [...d.steps];
      const swapIndex = dir === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= next.length) return d;
      const current = next[index];
      const other = next[swapIndex];
      if (!current || !other) return d;
      [next[index], next[swapIndex]] = [other, current];
      return { ...d, steps: next };
    });
  };

  const validate = (): string | null => {
    if (!draft.name.trim()) return 'Name is required';
    if (draft.autoApprove) {
      if (draft.condition.kind === 'none') {
        return 'Auto-approve needs a condition — leaving it empty would approve every submission.';
      }
      return null;
    }
    if (draft.steps.length === 0) return 'At least one step is required';
    for (let i = 0; i < draft.steps.length; i++) {
      if (draft.steps[i]?.approverSources.length === 0) {
        return `Step ${i + 1} needs at least one approver`;
      }
    }
    return null;
  };

  const onSave = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const { conditions, error: condError } = conditionsToServer(draft.condition);
    if (condError) {
      toast.error(condError);
      return;
    }
    const input: CreateApprovalPolicyInput = {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      conditions,
      steps: draft.autoApprove ? [] : draft.steps.map(({ _uiKey: _, ...rest }) => rest),
      autoApprove: draft.autoApprove,
      isActive: draft.isActive,
    };
    if (isEdit) patchMutation.mutate(input);
    else createMutation.mutate(input);
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(initialDraft);
  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty,
    isSubmitting: isSaving,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{isEdit ? `Edit ${policy.name}` : 'New approval policy'}</SheetTitle>
            <SheetDescription>
              First matching rule wins. Changes take effect immediately once saved.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-6 overflow-y-auto px-4 pb-4">
            <section className="surface-card space-y-4 rounded-md border p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Details
              </h2>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="e.g. MSCFO → manager"
                  />
                </div>
                <div className="space-y-1.5 sm:w-36">
                  <Label htmlFor="isActive">Status</Label>
                  <Select
                    value={draft.isActive ? 'active' : 'inactive'}
                    onValueChange={(v) => setDraft((d) => ({ ...d, isActive: v === 'active' }))}
                  >
                    <SelectTrigger id="isActive" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  placeholder="Optional"
                  rows={2}
                />
              </div>
            </section>

            <section className="surface-card space-y-3 rounded-md border p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Condition
              </h2>
              <ApprovalConditionEditor
                value={draft.condition}
                onChange={(condition) => setDraft((d) => ({ ...d, condition }))}
              />
            </section>

            <section className="surface-card space-y-3 rounded-md border p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Approvers
              </h2>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="autoApprove"
                  checked={draft.autoApprove}
                  onCheckedChange={(v) => setDraft((d) => ({ ...d, autoApprove: v === true }))}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="autoApprove" className="cursor-pointer">
                    Auto-approve (skip approvers)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Submissions matching this policy's condition are approved immediately — no
                    approver workflow runs.
                  </p>
                </div>
              </div>

              {draft.autoApprove ? (
                <div className="space-y-1 rounded-md border border-dashed bg-muted/30 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Zap className="text-amber-500" /> Auto-approve enabled
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Use sparingly — restrict the condition to a narrow trusted set (for example, the
                    row owner's role on the customer team).
                  </p>
                </div>
              ) : (
                <div className="space-y-3 border-t pt-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Approval Steps
                  </h3>
                  {draft.steps.map((step, index) => (
                    <StepCard
                      key={step._uiKey}
                      index={index}
                      step={step}
                      onChange={(next) => setStep(index, next)}
                      onRemove={() => removeStep(index)}
                      onMoveUp={index > 0 ? () => moveStep(index, 'up') : undefined}
                      onMoveDown={
                        index < draft.steps.length - 1 ? () => moveStep(index, 'down') : undefined
                      }
                      canRemove={draft.steps.length > 1}
                    />
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addStep}>
                    <Plus className="mr-1.5 size-3.5" /> Add step
                  </Button>
                </div>
              )}
            </section>
          </div>

          <SheetFooter className="flex-row justify-end border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => requestOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <RequirePermission permission="approval_policy.edit" fallback={null}>
              <Button type="button" onClick={onSave} disabled={isSaving}>
                {isSaving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
              </Button>
            </RequirePermission>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

interface StepCardProps {
  index: number;
  step: DraftStep;
  onChange: (next: DraftStep) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canRemove: boolean;
}

function StepCard({
  index,
  step,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canRemove,
}: StepCardProps): React.ReactElement {
  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="kicker text-muted-foreground">Step {index + 1}</span>
          <Select
            value={step.approvalType}
            onValueChange={(v) => onChange({ ...step, approvalType: v as 'any' | 'all' })}
          >
            <SelectTrigger className="h-7 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any approver</SelectItem>
              <SelectItem value="all">All approvers</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-0.5">
          {onMoveUp && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onMoveUp}
              className="h-6 w-6 p-0"
            >
              <ArrowUp className="size-3.5" />
            </Button>
          )}
          {onMoveDown && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onMoveDown}
              className="h-6 w-6 p-0"
            >
              <ArrowDown className="size-3.5" />
            </Button>
          )}
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="h-6 w-6 p-0"
            >
              <X className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
      <ApproverSourcePicker
        value={step.approverSources}
        onChange={(next) => onChange({ ...step, approverSources: next })}
      />
    </div>
  );
}
